import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Address } from '@pineapple/address-search';
import { RootState } from '../../app/store';
import { setAddress, StoredAddress } from '../address/addressSlice';
import { setFormattedAddress } from '../signup/signupSlice';
import { isServiceable } from '../../lib/analytics';
import { Network } from '../../models/plans';
import { CfQualification, qualifyCommunityFibre } from './communityFibreAPI';
import { communityFibreSurface } from '../featureFlags/flags';
import { addressFromLocationId } from './locationLink';

/**
 * The verdict for the address being checked, across networks.
 *
 * The DGtek verdict arrives with the address itself (the address-search
 * widget qualifies it against sequoia's building search and sets
 * `status`). With the Community Fibre flag on, the address is also sent to
 * sequoia's Community Fibre qualification; Community Fibre is preferred
 * when it can serve the address, DGtek is the fallback, and a Community
 * Fibre outage (502/503) simply leaves DGtek's verdict.
 */
export type CheckedAddress = StoredAddress;

export interface QualificationState {
  status: 'idle' | 'checking' | 'done';
  /** The check in progress came from a ?locationId= link, not a search. */
  viaLink: boolean;
  /** A ?locationId= link that could not be turned into an address. */
  linkError: string | null;
  /** The network the address will be sold on; null = not available. */
  network: Network | null;
  /** Community Fibre wants the unit before it can answer. */
  unitRequired: boolean;
  communityFibre: CfQualification | null;
  /** Community Fibre was asked and could not answer (502/503/network). */
  communityFibreUnavailable: boolean;
}

const initialState: QualificationState = {
  status: 'idle',
  viaLink: false,
  linkError: null,
  network: null,
  unitRequired: false,
  communityFibre: null,
  communityFibreUnavailable: false,
};

const dgtekServiceable = (address: Address) => (
  isServiceable(address.status) || address.buildingCategory === 'greenfield'
);

export interface CheckOutcome {
  network: Network | null;
  unitRequired: boolean;
  communityFibre: CfQualification | null;
  communityFibreUnavailable: boolean;
}

/**
 * Records the address and works out where it can be served. Dispatched
 * for a search result and for an address arriving through the URL
 * (?locationId=) alike.
 */
export const checkAddressAsync = createAsyncThunk<CheckOutcome, CheckedAddress, { state: RootState }>(
  'qualification/checkAddress',
  async (address, { dispatch, getState }) => {
    dispatch(setFormattedAddress(address));
    dispatch(setAddress(address));

    const outcome: CheckOutcome = {
      network: dgtekServiceable(address) ? 'dgtek' : null,
      unitRequired: false,
      communityFibre: null,
      communityFibreUnavailable: false,
    };
    const flags = getState().featureFlags.value;
    if (!flags.communityFibre) return outcome;

    try {
      // Sequoia asks MEF unless told otherwise; only NPIS needs saying.
      const surface = communityFibreSurface(flags);
      const result = await qualifyCommunityFibre({
        ...(address.gnafId ? { gnafId: address.gnafId } : {}),
        ...(surface === 'npis' ? { api: surface } : {}),
        address: {
          unit: address.unit || undefined,
          streetNumber: address.street_number,
          street: address.street,
          suburb: address.suburb,
          state: address.state,
          postcode: address.postcode,
        },
      });
      if (result.kind !== 'answer') {
        outcome.communityFibreUnavailable = true;
        return outcome;
      }
      outcome.communityFibre = result.qualification;
      if (result.qualification.serviceable) {
        // Community Fibre wins wherever it can serve the address.
        outcome.network = 'communityfibre';
      } else if (result.qualification.unitRequired && !address.unit) {
        // In the Community Fibre footprint, but they only answer for a
        // unit: ask for it before falling back to a DGtek verdict.
        outcome.unitRequired = true;
      }
    } catch {
      outcome.communityFibreUnavailable = true;
    }
    return outcome;
  },
);

/**
 * A link that names the address (?locationId=<G-NAF id>): the id is
 * resolved through sequoia and the address is checked straight away, so
 * the customer lands on the verdict without searching. An id that cannot
 * be resolved leaves the form with a message.
 */
export const checkLocationIdAsync = createAsyncThunk<CheckOutcome | null, string, { state: RootState }>(
  'qualification/checkLocationId',
  async (locationId, { dispatch }) => {
    const address = await addressFromLocationId(locationId);
    if (!address) return null;
    return dispatch(checkAddressAsync(address)).unwrap();
  },
  {
    // React runs the mount effect twice in development (StrictMode); one
    // check per link is enough, and two racing answers could disagree.
    condition: (_, { getState }) => getState().qualification.status !== 'checking',
  },
);

export const qualificationSlice = createSlice({
  name: 'qualification',
  initialState,
  reducers: {
    resetQualification: () => initialState,
    clearLinkError: (state) => {
      state.linkError = null;
    },
    // The customer has given the unit; the verdict is asked again.
    setUnitRequired: (state, action: PayloadAction<boolean>) => {
      state.unitRequired = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAddressAsync.pending, (state) => {
        state.status = 'checking';
        state.unitRequired = false;
      })
      .addCase(checkAddressAsync.fulfilled, (state, action) => {
        state.status = 'done';
        // A verdict supersedes any earlier complaint about the link.
        state.linkError = null;
        state.network = action.payload.network;
        state.unitRequired = action.payload.unitRequired;
        state.communityFibre = action.payload.communityFibre;
        state.communityFibreUnavailable = action.payload.communityFibreUnavailable;
      })
      .addCase(checkAddressAsync.rejected, (state) => {
        state.status = 'done';
      })
      .addCase(checkLocationIdAsync.pending, (state) => {
        state.status = 'checking';
        state.viaLink = true;
        state.linkError = null;
      })
      .addCase(checkLocationIdAsync.fulfilled, (state, action) => {
        state.viaLink = false;
        if (action.payload === null) {
          state.status = 'idle';
          state.linkError = 'We could not find the address in your link. Please search for it below.';
        }
      })
      .addCase(checkLocationIdAsync.rejected, (state) => {
        state.status = 'idle';
        state.viaLink = false;
        state.linkError = 'We could not check the address in your link right now. Please search for it below.';
      });
  },
});

export const { resetQualification, setUnitRequired, clearLinkError } = qualificationSlice.actions;

export const selectQualification = (state: RootState) => state.qualification;
export const selectNetwork = (state: RootState) => state.qualification.network;
export const selectCommunityFibre = (state: RootState) => state.qualification.communityFibre;

export default qualificationSlice.reducer;
